import css from './style.module.scss';
import DropdownArrow from '../../svg/dropdown-arrow.svg';
import { gql } from '../../util/fetchGql';
import cls from '../../util/cls';
import { useEffect, useMemo, useState } from 'react';

export const VARIANT_SELECT_FRAGMENT = gql`
	fragment VariantSelect on products_Variant {
		id
		title
        options {
            id
            title
	        level
	        parent { id }
        }
	}
`;

const naturalSort = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'}).compare;

export default function VariantSelect ({ label, variants, white = false, onChange, defaultSelected }) {
	const [selectedOpts, setSelectedOpts] = useState({})
		, [matchingVariantId, setMatchingVariantId] = useState(null);

	const variantIdsByOptions = useMemo(() => {
		const idsByOpts = [];

		for (const variant of variants) {
			idsByOpts.push({
				id: variant.id,
				opts: variant.options.filter(o => o.level === 2).reduce((a, o) => {
					a[o.parent.id] = o.id;
					return a;
				}, {}),
			});
		}

		const first = Object.values(idsByOpts)[0];
		setSelectedOpts(first.opts);
		setMatchingVariantId(first.id);

		return idsByOpts;
	}, [variants]);

	const _onChange = e => {
		onChange && onChange(e.target.value);
	};

	const onOptionChange = id => e => {
		setSelectedOpts(old => {
			const next = {
				...old,
				[id]: e.target.value,
			};

			const keyVal = Object.entries(next);

			const variant = variantIdsByOptions.filter(v => {
				for (const [key, val] of keyVal)
					if (v.opts[key] !== val)
						return false;

				return true;
			})?.[0]?.id ?? null;
			setMatchingVariantId(variant);

			return next;
		});
	};

	useEffect(() => {
		onChange && onChange(matchingVariantId);
	}, [matchingVariantId]);

	const options = useMemo(() => {
		const options = {};

		for (const variant of variants) {
			variant.options.sort((a, b) => a.level - b.level).forEach(opt => {
				if (opt.level === 1) {
					if (options.hasOwnProperty(opt.id)) return;

					options[opt.id] = {
						key: opt.id,
						label: opt.title,
						values: {},
					};
				} else {
					const pid = opt.parent.id;
					if (
						!options.hasOwnProperty(pid)
						|| options[pid].values.hasOwnProperty(opt.id)
					) return;

					options[pid].values[opt.id] = {
						label: opt.title,
						value: opt.id,
					};
				}
			});
		}

		const opts = Object.values(options).map(opt => {
			opt.values = Object.values(opt.values).sort((a, b) => naturalSort(a.label, b.label));
			return opt;
		});

		const defaults = {};

		for (const opt of opts)
			defaults[opt.key] = opt.values[0].value;

		return opts;
	}, [variants]);

	return options.length > 0 ? (
		<div className={css.options}>
			{options.map(({ key, label, values }) => (
				<label key={key} className={cls(css.variantSelect, { [css.white]: white })}>
					<span>{label}</span>
					<span>
						<select onChange={onOptionChange(key)} defaultValue={selectedOpts[key]}>
							{values.map(({ label, value }) => (
								<option key={value} value={value}>{label}</option>
							))}
						</select>
						<DropdownArrow />
					</span>
				</label>
			))}
		</div>
	) : (
		<label className={cls(css.variantSelect, { [css.white]: white })}>
			<span>{label ?? 'Colour'}</span>
			<select onChange={_onChange} value={defaultSelected}>
				{variants.map(variant => (
					<option key={variant.id} value={variant.id}>{variant.title}</option>
				))}
			</select>
			<DropdownArrow />
		</label>
	);
}
